import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
// Chakra imports
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  VStack,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import Icon from "components/icons/Icon";
// Custom components
import { HSeparator } from "components/separator/Separator";
import DefaultAuth from "layouts/auth/Default";
// Assets
import illustration from "assets/img/auth/auth.png";
import { FcGoogle } from "react-icons/fc";
import { MdOutlineRemoveRedEye } from "react-icons/md";
import { RiEyeCloseLine } from "react-icons/ri";
// Auth Context
import { useAuth } from "contexts/AuthContext";

function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { login, isLoading, error: authError, clearError } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState<string>('');
  const [approvalMessage, setApprovalMessage] = useState<string>('');  // Chakra color mode
  const textColor = useColorModeValue("navy.700", "white");
  const textColorSecondary = useColorModeValue("gray.400", "gray.400");
  const textColorDetails = useColorModeValue("navy.700", "secondaryGray.600");
  const textColorBrand = useColorModeValue("brand.500", "white");
  const brandStars = useColorModeValue("brand.500", "brand.400");
  const googleBg = useColorModeValue("secondaryGray.300", "whiteAlpha.200");
  const googleText = useColorModeValue("navy.700", "white");
  const googleHover = useColorModeValue(
    { bg: "gray.200" },
    { bg: "whiteAlpha.300" }
  );
  const googleActive = useColorModeValue(
    { bg: "secondaryGray.300" },
    { bg: "whiteAlpha.200" }
  );
  const bgColor = useColorModeValue('gray.50', 'navy.900');
  const formBg = useColorModeValue('white', 'navy.800');
  const boxShadow = useColorModeValue(
    '0px 18px 40px rgba(112, 144, 176, 0.12)',
    '0px 18px 40px rgba(0, 0, 0, 0.3)'
  );
  const buttonBg = useColorModeValue('brand.500', 'brand.400');
  const buttonHover = useColorModeValue('brand.600', 'brand.500');
  const buttonActive = useColorModeValue('brand.700', 'brand.600');
  const outlineButtonColor = useColorModeValue('brand.500', 'brand.400');
  const outlineButtonBorder = useColorModeValue('brand.500', 'brand.400');
  const outlineButtonHover = useColorModeValue('brand.50', 'rgba(66, 153, 225, 0.1)');
  const [show, setShow] = React.useState(false);
  const handleClick = () => setShow(!show);

  // Check for approval message from navigation
  useEffect(() => {
    const state = location.state as any;
    if (state?.message) {
      setApprovalMessage(state.message);
      // Clear the message from location state
      navigate(location.pathname, { replace: true });
    }
  }, [location, navigate]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear errors when user starts typing
    if (error) setError('');
    if (authError) clearError();
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!formData.email || !formData.password) {
      const errorMsg = 'Please enter both email and password';
      setError(errorMsg);
      toast({
        title: "Validation Error",
        description: errorMsg,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      return;
    }

    setError('');

    try {
      const result = await login(formData);

      if (result.success) {
        toast({
          title: "Login Successful",
          description: `Welcome back!`,
          status: "success",
          duration: 3000,
          isClosable: true,
        });

        // Small delay to ensure auth state is updated, then redirect
        setTimeout(() => {
          navigate('/admin/default');
        }, 100);
      } else {
        const errorMsg = result.error || 'Login failed. Please try again.';
        setError(errorMsg);

        // Show toast notification for immediate feedback
        toast({
          title: "Login Failed",
          description: errorMsg,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (err: any) {
      console.error('Login error:', err);

      // Handle different error formats
      let errorMsg = 'An unexpected error occurred. Please try again.';

      if (Array.isArray(err)) {
        const foundError = err.find((item: any) => typeof item === 'string' && item.length > 0);
        if (foundError) {
          errorMsg = foundError;
        }
      } else if (err && typeof err === 'object') {
        if (Array.isArray(err.u)) {
          const foundError = err.u.find((item: any) => typeof item === 'string' && item.length > 0);
          if (foundError) {
            errorMsg = foundError;
          }
        } else if (err.message) {
          errorMsg = err.message;
        }
      }

      setError(errorMsg);

      // Show toast notification
      toast({
        title: "Connection Error",
        description: errorMsg,
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    }
  };

  return (
    <Flex
      w='100vw'
      h='100vh'
      alignItems='center'
      justifyContent='center'
      flexDirection='column'
      bg={bgColor}
      px={{ base: '20px', md: '0px' }}>
      <Box textAlign='center' mb={{ base: '30px', md: '40px' }}>
        <Heading color={textColor} fontSize={{ base: '28px', md: '36px' }} mb='10px'>
          Sign In
        </Heading>
        <Text
          color={textColorSecondary}
          fontWeight='400'
          fontSize={{ base: 'sm', md: 'md' }}>
          Enter your email and password to sign in!
        </Text>
      </Box>
      <Flex
        direction='column'
        w={{ base: "100%", sm: "400px", md: "420px" }}
        maxW='420px'
        background={formBg}
        borderRadius='20px'
        p={{ base: '30px', md: '40px' }}
        boxShadow={boxShadow}>

        {approvalMessage && (
          <Alert status="info" mb="20px" borderRadius="8px">
            <AlertIcon />
            {approvalMessage}
          </Alert>
        )}

        {(error || authError) && (
          <Alert status="error" mb="20px" borderRadius="8px">
            <AlertIcon />
            {error || authError}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <FormControl>
            <FormLabel
              display='flex'
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              mb='8px'>
              Email<Text color={brandStars}>*</Text>
            </FormLabel>
            <Input
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              isRequired={true}
              variant='auth'
              fontSize='sm'
              type='email'
              placeholder='Enter your email'
              mb='24px'
              fontWeight='500'
              size='lg'
              h='48px'
              disabled={isLoading}
            />
            <FormLabel
              fontSize='sm'
              fontWeight='500'
              color={textColor}
              display='flex'
              mb='8px'>
              Password<Text color={brandStars}>*</Text>
            </FormLabel>
            <InputGroup size='md' mb='24px'>
              <Input
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                isRequired={true}
                fontSize='sm'
                placeholder='Enter your password'
                size='lg'
                h='48px'
                type={show ? "text" : "password"}
                variant='auth'
                disabled={isLoading}
              />
              <InputRightElement h='48px'>
                <Icon
                  color={textColorSecondary}
                  _hover={{ cursor: "pointer" }}
                  as={show ? RiEyeCloseLine : MdOutlineRemoveRedEye}
                  onClick={handleClick}
                />
              </InputRightElement>
            </InputGroup>
            <Flex justifyContent='center' align='center' mb='24px'>
              <FormControl display='flex' alignItems='center' justifyContent='center'>
                <Checkbox
                  id='remember-login'
                  colorScheme='brandScheme'
                  me='10px'
                />
                <FormLabel
                  htmlFor='remember-login'
                  mb='0'
                  fontWeight='normal'
                  color={textColor}
                  fontSize='sm'>
                  Keep me logged in
                </FormLabel>
              </FormControl>
            </Flex>
            <Button
              type="submit"
              fontSize='sm'
              bg={buttonBg}
              color='white'
              _hover={{ bg: buttonHover }}
              _active={{ bg: buttonActive }}
              fontWeight='500'
              w='100%'
              h='50px'
              mb='24px'
              isLoading={isLoading}
              loadingText="Signing In..."
              disabled={isLoading}
              _disabled={{ 
                bg: useColorModeValue('gray.300', 'gray.600'), 
                cursor: 'not-allowed' 
              }}>
              Sign In
            </Button>
          </FormControl>
        </form>

        {/* Signup Links */}
        <Box textAlign='center' mt={6}>
          <Text fontSize='sm' color={textColorSecondary} mb={3}>
            Don't have an account?
          </Text>
          <Button
            variant='outline'
            color={outlineButtonColor}
            borderColor={outlineButtonBorder}
            _hover={{ 
              bg: outlineButtonHover,
              borderColor: buttonHover 
            }}
            _active={{ 
              bg: outlineButtonHover,
              borderColor: buttonActive 
            }}
            size='sm'
            w='100%'
            onClick={() => navigate('/auth/trainer-signup')}
          >
            Sign Up as a Trainer
          </Button>
          <Button
            variant='ghost'
            color={textColorSecondary}
            size='sm'
            w='100%'
            mt={2}
            onClick={() => navigate('/auth/agency-signup')}
          >
            Create Agency Account
          </Button>
        </Box>
      </Flex>
    </Flex>
  );
}

export default SignIn;
